import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Formik, Form } from "formik";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import * as Yup from "yup";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { useUpdateReconciliationStartDateMutation } from "store/apis/reconciliation";
import { useToast } from "hooks/useToast";

const validationSchema = Yup.object().shape({
  reconciliation_start_date: Yup.string().required(
    "Reconciliation start date is required"
  ),
});

type ReconciliationStartDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  entityId: string;
};

export const ReconciliationStartDateModal = ({
  isOpen,
  onClose,
  accountId,
  entityId,
}: ReconciliationStartDateModalProps) => {
  const { alertToast, successToast } = useToast();
  const [updateStartDate] = useUpdateReconciliationStartDateMutation();

  const onUpdate = async (date: string, setToBookkeepingDate: boolean) => {
    try {
      await updateStartDate({
        entityId,
        accountId,
        reconciliation_start_date: date,
      }).unwrap();
      successToast({
        message: "Reconciliation start date updated successfully",
      });
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Reconciliation start date</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Formik
          initialValues={{
            reconciliation_start_date: "",
            set_to_bookkeeping_start_date: false,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            await onUpdate(
              values.reconciliation_start_date,
              values.set_to_bookkeeping_start_date
            );
            onClose();
          }}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-4">
                  <p className="t-text-body-sm t-text-text-30 t-mb-0">
                    Select the date from which you want to start the
                    reconciliation.
                  </p>
                  <div className="t-flex t-flex-col t-gap-2">
                    <DateField
                      name="reconciliation_start_date"
                      dateProps={{
                        label: "Reconciliation start date",
                        placeholder: "Select start date",
                        customSize: "small",
                        showErrorOnceTouched: true,
                        portalId: "billing-start-0",
                        required: true,
                        maxDate: new Date(),
                        rightComponent: (
                          <span className="t-text-text-30">
                            <CalendarIcon />
                          </span>
                        ),
                      }}
                    />
                  </div>
                </div>
              </Modal.Body>

              <Modal.Footer>
                <div className="t-flex t-justify-end t-gap-3">
                  <Button
                    type="button"
                    customType="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    disabled={isSubmitting || !isValid}
                    isLoading={isSubmitting}
                  >
                    Update
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
